import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
} from '@angular/core';
import { AppFeatureExtensions } from '@app/app-feature-extensions.config';
import { DropdownMenuWithIconsService } from '@shared/dropdown-menu-with-icons/dropdown-menu-with-icons.service';
import { CandidateAdapter } from '@shared';
import { NoteSubject } from 'mojo-irp-notes';
import { hasAnonymization } from '@collections/users';
import { Store } from '@ngrx/store';
import {
  CandidatesConfig,
  CandidatesPrivileges,
} from '@modules/candidates/candidates.abstract.config';
import { IsMobileViewService } from '@shared/services/is-mobile-view.service';
import { BehaviorSubject, Observable } from 'rxjs';
import { map, withLatestFrom } from 'rxjs/operators';
import { MojoPrivilegesService } from 'mojo-privileges';
import { appConfig } from '@app/app.config';
import { getSelectedCandidateLatestPdfResumePath } from '@app/collections/attachments';
import { resetTalentPoolsForCandidateState, TalentPool } from 'mojo-talent-pools';
import { DropdownMenuItem } from 'mojo-abstract-dropdown-menu';
import { TasksEntityType } from 'mojo-tasks';
import { trackViewCandidateDetails } from '@core/activity-tracking/activity-tracking.actions';
import { PdfViewerAdapterService } from '@shared/pdf-viewer-adapter/services/pdf-viewer-adapter.service';

@Component({
  selector: 'mojo-side-nav-candidate-view',
  templateUrl: './side-nav-candidate-view.component.html',
  styleUrls: ['./side-nav-candidate-view.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SideNavCandidateViewComponent implements OnChanges, OnInit {
  @Input() opened: boolean;
  @Input() isNextButtonDisabled = false;
  @Input() isPreviousButtonDisabled = false;
  @Input() talentPools: TalentPool[];
  @Input() sideDrawerCandidate: CandidateAdapter;
  @Input() jobId?: number;
  @Input() talentPoolId?: number;
  @Output() closed = new EventEmitter();
  @Output() changeCandidate = new EventEmitter<number>();
  @Input() fromTalentPool = false;
  resumeSrc$: Observable<string> = this.store.select(getSelectedCandidateLatestPdfResumePath);
  resumeSrc: string;
  noteSubject = NoteSubject;
  subject = NoteSubject.Candidate;
  privileges: CandidatesPrivileges = this.candidatesConfig.privileges;
  closeNotes$ = new BehaviorSubject(false);
  shouldDisplayFiles$ = this.store.select(hasAnonymization).pipe(
    withLatestFrom(
      this.mojoPrivilegesService.hasAnyPrivilege([
        this.privileges.candidates.files,
        this.privileges.candidates.resumes,
        this.privileges.candidates.filesReadOnly,
      ]),
    ),
    map(([isAnonymized, hasPrivileges]) => !isAnonymized && hasPrivileges),
  );
  tasksEntityType = TasksEntityType.candidate;
  tasksExtension = AppFeatureExtensions.TASKS;
  talentPoolExtension = AppFeatureExtensions.TALENT_POOLS;
  tasksPrivilege = appConfig.privileges.tasks.view;
  talentPoolsPrivilege = appConfig.privileges.talentPools.view;

  jobMenuItems: DropdownMenuItem[];

  commonMenuItems: DropdownMenuItem[];

  commonMenuItemsInsideJob: DropdownMenuItem[];

  initDropdownMenuOptions() {
    this.jobMenuItems = [
      this.dropdownMenuWithIconsService.getMoveForwardForDirectApplicantsForSpecificJob(this.jobId),
      this.dropdownMenuWithIconsService.getMoveForwardForShortlistForSpecificJob(this.jobId),
      this.dropdownMenuWithIconsService.getMoveForwardForProspectsForSpecificJob(this.jobId),
      this.dropdownMenuWithIconsService.getSubmitToHiringManagerForSpecificJob(this.jobId),
    ];
    this.commonMenuItems = [
      this.dropdownMenuWithIconsService.getMoveForwardForAnyJob(),
      this.dropdownMenuWithIconsService.getInviteToApplyForAnyJob(),
      this.dropdownMenuWithIconsService.getAddAsProspectForAnyJob(),
      this.dropdownMenuWithIconsService.getAddToTalentPoolForAnyJob(),
    ];
    this.commonMenuItemsInsideJob = [
      this.dropdownMenuWithIconsService.getInviteToApplyForSpecificJob(this.jobId),
      this.dropdownMenuWithIconsService.getAddAsProspectForAnyJob(),
      this.dropdownMenuWithIconsService.getAddToTalentPoolForAnyJob(),
    ];
  }

  constructor(
    private store: Store<{}>,
    private candidatesConfig: CandidatesConfig,
    private mojoPrivilegesService: MojoPrivilegesService,
    private dropdownMenuWithIconsService: DropdownMenuWithIconsService,
    public isMobileViewService: IsMobileViewService,
    protected pdfViewerAdapterService: PdfViewerAdapterService,
  ) {}
  ngOnInit(): void {
    this.resumeSrc$.subscribe((resumeSrc) => {
      this.resumeSrc = resumeSrc;
    });
    this.store.dispatch(
      trackViewCandidateDetails({
        payload: {
          candidateId: this.sideDrawerCandidate?.id,
          jobId: this.jobId,
          talentPoolId: this.fromTalentPool ? this.talentPoolId : undefined,
          viewContext: this.fromTalentPool && this.talentPoolId != null ? 'PROJECT' : 'NON_PROJECT',
        },
      }),
    );
  }

  ngOnChanges(): void {
    this.initDropdownMenuOptions();
  }

  close() {
    this.closed.emit();
    this.closeNotes$.next(true);
  }

  changeCandidateFn(number) {
    this.store.dispatch(resetTalentPoolsForCandidateState());
    this.changeCandidate.emit(number);
    this.closeNotes$.next(true);
  }
}
